import React, { useState, useEffect, useContext } from 'react';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { showToast } from '@/lib/custom-toast';
import { TrendingUp, DollarSign, PiggyBank, Plus, Gem } from 'lucide-react';

const InvestmentsSection = () => {
  const { token } = useContext(AuthContext);
  const [investments, setInvestments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInvestment, setSelectedInvestment] = useState(null);
  const [showGainModal, setShowGainModal] = useState(false);
  const [showLiquidateModal, setShowLiquidateModal] = useState(false);
  
  // Helper function to get current date in local timezone (YYYY-MM-DD format)
  const getLocalDateString = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const [gainData, setGainData] = useState({
    gain_amount: '',
    description: '',
    date: getLocalDateString(),
    convert_to_income: true
  });

  const [liquidateData, setLiquidateData] = useState({
    amount: '',
    date: getLocalDateString(),
    notes: ''
  });

  useEffect(() => {
    fetchInvestments();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchInvestments = async () => {
    try {
      const response = await axios.get(`${API}/investments/active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvestments(response.data);
    } catch (error) {
      console.error('Error al cargar inversiones:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddGain = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(
        `${API}/investments/${selectedInvestment.id}/gains`,
        {
          investment_id: selectedInvestment.id,
          gain_amount: parseFloat(gainData.gain_amount),
          description: gainData.description,
          date: gainData.date,
          convert_to_income: gainData.convert_to_income
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showToast.success('Ganancia agregada exitosamente');
      setShowGainModal(false);
      setGainData({
        gain_amount: '',
        description: '',
        date: getLocalDateString(),
        convert_to_income: true
      });
      fetchInvestments();
    } catch (error) {
      showToast.error(error.response?.data?.detail || 'Error al agregar ganancia');
    }
  };

  const handleLiquidate = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(
        `${API}/investments/${selectedInvestment.id}/liquidate`,
        {
          amount: parseFloat(liquidateData.amount),
          date: liquidateData.date,
          notes: liquidateData.notes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showToast.success('Inversión liquidada exitosamente');
      setShowLiquidateModal(false);
      setLiquidateData({
        amount: '',
        date: getLocalDateString(),
        notes: ''
      });
      fetchInvestments();
    } catch (error) {
      showToast.error(error.response?.data?.detail || 'Error al liquidar');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount || 0);
  };

  const formatMoneyInput = (value) => {
    if (!value) return '';
    const strValue = String(value);
    
    // Si termina con punto, preservarlo
    if (strValue.endsWith('.')) {
      const intPart = strValue.slice(0, -1).replace(/[^\d]/g, '');
      if (!intPart) return '.';
      return parseInt(intPart).toLocaleString('es-MX') + '.';
    }
    
    // Si tiene decimales, formatear ambas partes
    const parts = strValue.split('.');
    if (parts.length === 2) {
      const intPart = parts[0].replace(/[^\d]/g, '');
      const decPart = parts[1];
      if (!intPart) return '.' + decPart;
      return parseInt(intPart).toLocaleString('es-MX') + '.' + decPart;
    }
    
    // Solo enteros
    const numbersOnly = strValue.replace(/[^\d]/g, '');
    if (!numbersOnly) return '';
    return parseInt(numbersOnly).toLocaleString('es-MX');
  };

  const parseMoneyInput = (value) => {
    // Permitir números y punto decimal
    return value.replace(/[^\d.]/g, '');
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-2 text-slate-600">Cargando inversiones...</p>
        </div>
      </Card>
    );
  }

  if (investments.length === 0) {
    return (
      <Card className="p-6 bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
        <div className="text-center py-8">
          <Gem className="w-12 h-12 text-purple-400 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No tienes inversiones activas</h3>
          <p className="text-sm text-slate-600">
            Crea un ahorro y márcalo como inversión para comenzar a trackear tus inversiones y ganancias
          </p>
        </div>
      </Card>
    );
  }

  const totalInvested = investments.reduce((sum, inv) => sum + inv.amount, 0);
  const totalGains = investments.reduce((sum, inv) => sum + inv.total_gains, 0);
  const totalValue = totalInvested + totalGains;

  return (
    <>
      <div className="space-y-4">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="p-4 bg-gradient-to-br from-blue-500 to-cyan-600 text-white">
            <div className="flex items-center gap-2 mb-2">
              <PiggyBank className="w-5 h-5" />
              <span className="text-sm font-medium">Invertido</span>
            </div>
            <p className="text-2xl font-bold">{formatCurrency(totalInvested)}</p>
          </Card>

          <Card className="p-4 bg-gradient-to-br from-green-500 to-emerald-600 text-white">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5" />
              <span className="text-sm font-medium">Ganancias</span>
            </div>
            <p className="text-2xl font-bold">{formatCurrency(totalGains)}</p>
          </Card>

          <Card className="p-4 bg-gradient-to-br from-purple-500 to-pink-600 text-white">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-5 h-5" />
              <span className="text-sm font-medium">Valor Total</span>
            </div>
            <p className="text-2xl font-bold">{formatCurrency(totalValue)}</p>
          </Card>
        </div>

        {/* Investments List */}
        <div className="space-y-3">
          {investments.map((investment) => (
            <Card key={investment.id} className="p-4 hover:shadow-md transition-shadow">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex-1">
                  <h4 className="font-semibold text-slate-900 mb-1">{investment.description}</h4>
                  <div className="flex flex-wrap gap-3 text-sm">
                    <span className="text-slate-600">
                      Capital: <span className="font-semibold text-blue-600">{formatCurrency(investment.amount)}</span>
                    </span>
                    <span className="text-slate-600">
                      Ganancias: <span className="font-semibold text-green-600">{formatCurrency(investment.total_gains)}</span>
                    </span>
                    <span className="text-slate-600">
                      Retorno: <span className="font-semibold text-purple-600">{investment.return_percentage.toFixed(2)}%</span>
                    </span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setSelectedInvestment(investment);
                      setShowGainModal(true);
                    }}
                    className="bg-green-50 border-green-300 text-green-700 hover:bg-green-100"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Ganancia
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setSelectedInvestment(investment);
                      setLiquidateData({...liquidateData, amount: investment.amount.toString()});
                      setShowLiquidateModal(true);
                    }}
                    className="bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100"
                  >
                    <DollarSign className="w-4 h-4 mr-1" />
                    Liquidar
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Add Gain Modal */}
      <Dialog open={showGainModal} onOpenChange={setShowGainModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Agregar Ganancia</DialogTitle>
            <DialogDescription>
              {selectedInvestment?.description}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddGain} className="space-y-4">
            <div>
              <Label htmlFor="gain_amount">Monto de la Ganancia</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-600 font-medium">$</span>
                <Input
                  id="gain_amount"
                  type="text"
                  value={formatMoneyInput(gainData.gain_amount)}
                  onChange={(e) => setGainData({...gainData, gain_amount: parseMoneyInput(e.target.value)})}
                  placeholder="0"
                  required
                  className="pl-8"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="gain_description">Descripción</Label>
              <Input
                id="gain_description"
                value={gainData.description}
                onChange={(e) => setGainData({...gainData, description: e.target.value})}
                placeholder="Ej: Rendimiento mensual"
                required
              />
            </div>

            <div>
              <Label htmlFor="gain_date">Fecha</Label>
              <Input
                id="gain_date"
                type="date"
                value={gainData.date}
                onChange={(e) => setGainData({...gainData, date: e.target.value})}
                required
              />
            </div>

            <div className="flex items-center space-x-2 p-3 bg-green-50 rounded-lg">
              <input
                type="checkbox"
                id="convert_to_income"
                checked={gainData.convert_to_income}
                onChange={(e) => setGainData({...gainData, convert_to_income: e.target.checked})}
                className="w-4 h-4 text-green-600 rounded"
              />
              <Label htmlFor="convert_to_income" className="text-sm cursor-pointer">
                Convertir a ingreso (recomendado)
              </Label>
            </div>

            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={() => setShowGainModal(false)} className="flex-1">
                Cancelar
              </Button>
              <Button type="submit" className="flex-1 bg-green-600 hover:bg-green-700">
                Agregar Ganancia
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Liquidate Modal */}
      <Dialog open={showLiquidateModal} onOpenChange={setShowLiquidateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Liquidar Inversión</DialogTitle>
            <DialogDescription>
              {selectedInvestment?.description} - Capital: {formatCurrency(selectedInvestment?.amount)}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleLiquidate} className="space-y-4">
            <div>
              <Label htmlFor="liquidate_amount">Monto a Liquidar</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-600 font-medium">$</span>
                <Input
                  id="liquidate_amount"
                  type="text"
                  value={formatMoneyInput(liquidateData.amount)}
                  onChange={(e) => setLiquidateData({...liquidateData, amount: parseMoneyInput(e.target.value)})}
                  placeholder="0"
                  required
                  className="pl-8"
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Máximo: {formatCurrency(selectedInvestment?.amount)}
              </p>
            </div>

            <div>
              <Label htmlFor="liquidate_date">Fecha</Label>
              <Input
                id="liquidate_date"
                type="date"
                value={liquidateData.date}
                onChange={(e) => setLiquidateData({...liquidateData, date: e.target.value})}
                required
              />
            </div>

            <div>
              <Label htmlFor="liquidate_notes">Notas (opcional)</Label>
              <Input
                id="liquidate_notes"
                value={liquidateData.notes}
                onChange={(e) => setLiquidateData({...liquidateData, notes: e.target.value})}
                placeholder="Ej: Retiro anticipado"
              />
            </div>

            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={() => setShowLiquidateModal(false)} className="flex-1">
                Cancelar
              </Button>
              <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700">
                Liquidar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default InvestmentsSection;
